import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/services/api";
import {
  ArrowLeft, Loader2, MessageSquare, CheckSquare, AlertTriangle,
  ThumbsUp, FileText, Image, Receipt, Milestone, GitPullRequest,
  Activity, Layers, RefreshCw, ChevronRight, Clock, Users,
  User, Calendar, Plus, Filter, Send, Target, X, Check, UserCheck, MapPin,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const CARD_TYPE_META = {
  MESSAGE:           { icon: MessageSquare, color: "text-[#706E6B]",   bg: "bg-[#FAFAFA]"    },
  TASK:              { icon: CheckSquare,   color: "text-[#0176D3]",   bg: "bg-[#0176D3]/5"    },
  DECISION:          { icon: ThumbsUp,      color: "text-[#5E4DB2]",  bg: "bg-[#F1ECFF]"  },
  APPROVAL:          { icon: ThumbsUp,      color: "text-[#2E844A]", bg: "bg-[#EAF5EE]" },
  REQUIREMENT:       { icon: FileText,      color: "text-[#0176D3]",    bg: "bg-[#EEF4FF]"    },
  DESIGN:            { icon: Image,         color: "text-pink-700",    bg: "bg-pink-50"    },
  DOCUMENT:          { icon: FileText,      color: "text-[#0176D3]",    bg: "bg-[#EEF4FF]"    },
  QUOTE_REFERENCE:   { icon: Receipt,       color: "text-[#8C4B00]",   bg: "bg-[#FFF4E5]"   },
  PAYMENT_REFERENCE: { icon: Receipt,       color: "text-[#2E844A]",   bg: "bg-[#EAF5EE]"   },
  ISSUE:             { icon: AlertTriangle, color: "text-[#BA0517]",     bg: "bg-[#FCEBEC]"     },
  MILESTONE:         { icon: Milestone,     color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]"  },
  CHANGE_REQUEST:    { icon: GitPullRequest,color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]"  },
  SYSTEM_EVENT:      { icon: Activity,      color: "text-[#706E6B]",   bg: "bg-[#EEF4FF]"   },
  CHECK_IN:          { icon: UserCheck,     color: "text-[#2E844A]",    bg: "bg-[#EAF5EE]"    },
  // WVB-04: Financial Decision — fully styled, Finance thread only
  FINANCIAL_DECISION: { icon: Receipt,     color: "text-[#0176D3]",    bg: "bg-[#EEF4FF]"    },
  // E2E-04C: Projected card types — reference canonical source, no payload duplication
  CUSTOMER_COMMUNICATION: { icon: MessageSquare, color: "text-[#0176D3]",  bg: "bg-[#EEF4FF]"  },
  DELIVERY_EVIDENCE:      { icon: CheckSquare,   color: "text-[#2E844A]",  bg: "bg-[#EAF5EE]"  },
  FINANCIAL_SUMMARY:      { icon: Receipt,       color: "text-[#5E4DB2]",  bg: "bg-[#F1ECFF]"  },
  OPERATIONAL_EXCEPTION:  { icon: AlertTriangle, color: "text-[#BA0517]",  bg: "bg-[#FCEBEC]"  },
};

const THREAD_TYPE_COLOR = {
  CUSTOMER: "border-[#0176D3]/30 bg-[#0176D3]/5",
  INTERNAL_OPERATIONS: "border-[#C9C9C9]/30 bg-[#F3F3F3]",
  FINANCE: "border-[#2E844A]/30 bg-[#EAF5EE]",
  SERVICE: "border-[#D8CEF7] bg-[#FAF8FF]",
};

const CARD_FILTERS = ["ALL", "TASK", "APPROVAL", "DECISION", "ISSUE", "DESIGN", "CHECK_IN", "MESSAGE"];

export default function ThreadsView() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  // Use ID-based selection to avoid stale-closure issues in callbacks
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCards, setLoadingCards] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [event, setEvent] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  // Explicit page-level error state (never leaves page blank)
  const [pageError, setPageError] = useState(null); // null | 'not_activated' | 'not_found' | 'forbidden' | 'load_error'
  // Mobile: show thread list panel vs detail panel
  const [mobileShowList, setMobileShowList] = useState(true);

  // Derived: look up the full thread object from the ID
  const selectedThread = threads.find(t => t.id === selectedThreadId) || null;

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[ThreadsView] loading eventId=', eventId);
    }
    try {
      const [tr, er] = await Promise.all([
        api.get(`/events/${eventId}/threads`),
        api.get(`/imagine/events/${eventId}`).catch(() => ({ data: null })),
      ]);
      const t = Array.isArray(tr.data?.threads) ? tr.data.threads : [];
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ThreadsView] threads loaded count=', t.length, 'eventId=', eventId);
      }
      setThreads(t);
      setEvent(er.data);
      // Select first thread if none selected or current selection invalid
      if (t.length > 0) {
        setSelectedThreadId(prev => {
          if (!prev || !t.find(th => th.id === prev)) return t[0].id;
          return prev;
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ThreadsView] load error status=', status, err?.response?.data?.detail);
      }
      if (status === 403) setPageError('not_activated');
      else if (status === 404) setPageError('not_found');
      else if (status === 401) { navigate('/login'); return; }
      else setPageError('load_error');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const loadCards = useCallback((threadId, cardFilter) => {
    setLoadingCards(true);
    const params = cardFilter && cardFilter !== "ALL" ? `?card_type=${cardFilter}` : "";
    api.get(`/threads/${threadId}/cards${params}`)
      .then(r => setCards(Array.isArray(r.data?.cards) ? r.data.cards : []))
      .catch(() => setCards([]))
      .finally(() => setLoadingCards(false));
  }, []);

  useEffect(() => {
    if (selectedThreadId) loadCards(selectedThreadId, filter);
  }, [selectedThreadId, filter, loadCards]);

  // ── Explicit non-blank page states ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
        <p className="text-sm text-[#706E6B]">Loading Event Threads…</p>
      </div>
    );
  }

  if (pageError) {
    const msgs = {
      not_activated: { title: "Threads Not Activated", body: "The Event Blueprint has not been approved and activated yet. Activate the Blueprint to create Threads.", icon: Layers },
      not_found:     { title: "Event Not Found",        body: "This event does not exist or you do not have access to it.", icon: AlertTriangle },
      forbidden:     { title: "Access Denied",          body: "You do not have permission to view threads for this event.", icon: AlertTriangle },
      load_error:    { title: "Unable to Load Threads", body: "An unexpected error occurred. Please retry.", icon: AlertTriangle },
    };
    const m = msgs[pageError] || msgs.load_error;
    const Icon = m.icon;
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 p-8 text-center">
        <Icon className="w-10 h-10 text-[#706E6B]" />
        <div>
          <p className="text-sm font-semibold text-[#444444]">{m.title}</p>
          <p className="text-xs text-[#706E6B] mt-1 max-w-xs">{m.body}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadThreads} className="px-4 py-2 text-xs bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded-lg hover:bg-[#253550] transition-colors">
            Retry
          </button>
          <button onClick={() => navigate(`/events`)} className="px-4 py-2 text-xs bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9] rounded-lg hover:text-[#181818] transition-colors">
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-48px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex-shrink-0">
        <button onClick={() => navigate(`/imagine/events/${eventId}`)} className="p-2 hover:bg-[#FAFAFA] rounded-lg text-[#706E6B]">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-bold text-[#181818] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Event Threads
          </h1>
          <p className="text-xs text-[#706E6B] truncate">{event?.name || eventId}</p>
        </div>
        <div className="ml-auto flex gap-2 flex-shrink-0">
          <button onClick={() => navigate(`/events/${eventId}/360`)} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg transition-colors">
            <Target className="w-3.5 h-3.5" />
            Event 360
          </button>
          <button onClick={loadThreads} className="p-2 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {threads.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <Layers className="w-12 h-12 text-[#444444] mx-auto" />
            <p className="text-[#706E6B] text-sm">No Threads have been activated for this Event.</p>
            <p className="text-xs text-[#706E6B]">Approve &amp; activate the Event Blueprint to create Threads.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Thread list — full-width on mobile when mobileShowList=true */}
          <div className={`
            border-r border-[#E5E5E5] flex-shrink-0 overflow-y-auto
            ${mobileShowList ? 'flex' : 'hidden'} flex-col w-full
            sm:flex sm:w-60 md:w-72
          `}>
            {threads.map(t => {
              const sel = selectedThreadId === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`thread-${t.thread_type?.toLowerCase()}`}
                  onClick={() => { setSelectedThreadId(t.id); setMobileShowList(false); }}
                  className={`w-full text-left px-4 py-3 border-b border-[#E5E5E5]/50 hover:bg-[#F3F3F3] transition-colors ${sel ? "bg-[#FAFAFA]/60 border-l-2 border-l-[#0176D3]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-xs font-semibold truncate ${sel ? "text-[#181818]" : "text-[#444444]"}`}>
                      {t.name}
                    </p>
                    <ChevronRight className="w-3 h-3 text-[#706E6B] flex-shrink-0" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <ThreadTypeBadge type={t.thread_type} />
                    <ThreadStatusDot status={t.status} />
                  </div>
                  {(t.open_tasks > 0 || t.open_issues > 0 || t.pending_approvals > 0) && (
                    <div className="flex gap-2 mt-1.5">
                      {t.open_tasks > 0 && <Counter label="Tasks" value={t.open_tasks} color="text-[#0176D3]" />}
                      {t.open_issues > 0 && <Counter label="Issues" value={t.open_issues} color="text-[#BA0517]" />}
                      {t.pending_approvals > 0 && <Counter label="Approvals" value={t.pending_approvals} color="text-[#8C4B00]" />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Card feed — hidden on mobile when mobileShowList=true */}
          {selectedThread && (
            <div className={`flex-1 flex flex-col overflow-hidden ${mobileShowList ? 'hidden sm:flex' : 'flex'}`}>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-shrink-0 gap-2">
                {/* Mobile: back to thread list */}
                <button
                  className="sm:hidden p-1.5 text-[#706E6B] hover:text-[#181818] hover:bg-[#FAFAFA] rounded-lg transition-colors"
                  onClick={() => setMobileShowList(true)}
                  aria-label="Back to thread list"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#181818] truncate">{selectedThread.name}</p>
                  <p className="text-xs text-[#706E6B]">{selectedThread.card_count ?? 0} cards</p>
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {CARD_FILTERS.map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${filter === f ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#FAFAFA] text-[#706E6B] hover:text-[#444444]"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loadingCards ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#706E6B]" /></div>
                ) : !Array.isArray(cards) || cards.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-8 h-8 text-[#444444] mx-auto mb-2" />
                    <p className="text-[#706E6B] text-sm">No activity yet</p>
                    <p className="text-xs text-[#706E6B] mt-1">Post a message or create a task to get started.</p>
                  </div>
                ) : (
                  cards.map(card => (
                    <ThreadCardView key={card.id} card={card} eventId={eventId} threadId={selectedThreadId} onRefresh={() => loadCards(selectedThreadId, filter)} />
                  ))
                )}
              </div>

              {/* Compose bar */}
              <ComposeBar
                threadId={selectedThreadId}
                onPosted={() => loadCards(selectedThreadId, filter)}
              />
            </div>
          )}

          {/* Thread summary (RIGHT — desktop only) */}
          {selectedThread && (
            <div className="hidden lg:block w-64 border-l border-[#E5E5E5] p-4 overflow-y-auto flex-shrink-0">
              <ThreadSummaryPanel thread={selectedThread} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Thread Card Component ─────────────────────────────────────────────────────
function ThreadCardView({ card, eventId, threadId, onRefresh }) {
  const navigate = useNavigate();
  const meta = CARD_TYPE_META[card.card_type] || CARD_TYPE_META.MESSAGE;
  const Icon = meta.icon;
  const [approving, setApproving] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    channel: "EMAIL",
    recipient_email: "",
    recipient_name: "",
    subject: card.title || "",
  });

  async function handleApprove(approved) {
    setApproving(true);
    try {
      await api.post(`/thread-cards/${card.id}/approve`, { approved, comments: "" });
      toast.success(approved ? "Approved!" : "Rejected");
      onRefresh();
    } catch { toast.error("Action failed"); }
    finally { setApproving(false); }
  }

  async function handleDispatch(e) {
    e.preventDefault();
    if (!dispatchForm.recipient_email && dispatchForm.channel === "EMAIL") {
      toast.error("Recipient email is required for EMAIL channel");
      return;
    }
    setDispatching(true);
    try {
      const payload = {
        channel: dispatchForm.channel,
        subject: dispatchForm.subject || card.title,
        recipient_name: dispatchForm.recipient_name || undefined,
        draft_only: true,   // DCC-04A: Draft First — never auto-send
      };
      if (dispatchForm.channel === "EMAIL") payload.recipient_email = dispatchForm.recipient_email;
      if (dispatchForm.channel === "WHATSAPP") payload.recipient_phone = dispatchForm.recipient_email; // reuse field

      await api.post(
        `/dcc02/events/${eventId}/threads/${threadId}/cards/${card.id}/dispatch`,
        payload
      );
      toast.success(
        "Draft created — open Communications to review and send",
        {
          action: {
            label: "Open",
            onClick: () => navigate(`/crm/events/${eventId}#communications`),
          },
          duration: 6000,
        }
      );
      setShowDispatch(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create draft";
      toast.error(msg);
    } finally {
      setDispatching(false);
    }
  }

  return (
    <div
      data-testid={`thread-card-${card.card_type?.toLowerCase()}`}
      className={`rounded-xl border border-[#C9C9C9]/50 p-3 ${meta.bg}`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 flex-shrink-0 ${meta.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={`text-[9px] font-mono uppercase tracking-wide ${meta.color} opacity-70`}>
                {card.card_type?.replace(/_/g, " ")}
              </span>
              <p className="text-xs font-semibold text-[#181818] mt-0.5">{card.title}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusBadge status={card.status} cardType={card.card_type} />
              {/* DCC-04A: Prepare Communication button — MESSAGE cards only */}
              {card.card_type === "MESSAGE" && (
                <button
                  data-testid={`dispatch-comm-btn-${card.id}`}
                  onClick={() => setShowDispatch(v => !v)}
                  title="Prepare Communication Draft"
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-[#0176D3]/10 text-[#0176D3] border border-[#0176D3]/20 rounded-md hover:bg-[#0176D3]/20 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="hidden sm:inline">Prepare Comm</span>
                </button>
              )}
            </div>
          </div>
          {card.description && card.card_type !== "MESSAGE" && (
            <p className="text-xs text-[#706E6B] mt-1 leading-relaxed">{card.description}</p>
          )}
          {card.card_type === "MESSAGE" && (
            <p className="text-sm text-[#444444] mt-1">{card.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            {card.due_at && (
              <span className="flex items-center gap-1 text-[10px] text-[#706E6B]">
                <Calendar className="w-3 h-3" />
                {card.due_at?.slice(0, 10)}
              </span>
            )}
            {card.priority && card.priority !== "NORMAL" && (
              <span className={`text-[10px] font-medium ${card.priority === "HIGH" || card.priority === "CRITICAL" ? "text-[#BA0517]" : "text-[#706E6B]"}`}>
                {card.priority}
              </span>
            )}
          </div>
          {/* Approve/Reject for approval-type cards */}
          {["APPROVAL", "DESIGN"].includes(card.card_type) && card.status === "PENDING" && (
            <div className="flex gap-2 mt-2">
              <button
                data-testid={`approve-card-${card.id}`}
                onClick={() => handleApprove(true)}
                disabled={approving}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20 rounded-lg hover:bg-[#EAF5EE] transition-colors disabled:opacity-50"
              >
                <Check className="w-3 h-3" /> Approve
              </button>
              <button
                data-testid={`reject-card-${card.id}`}
                onClick={() => handleApprove(false)}
                disabled={approving}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[#FCEBEC] text-[#BA0517] border border-[#BA0517]/20 rounded-lg hover:bg-[#FCEBEC] transition-colors disabled:opacity-50"
              >
                <X className="w-3 h-3" /> Reject
              </button>
            </div>
          )}

          {/* WVB-04: FINANCIAL_DECISION fully styled card */}
          {card.card_type === "FINANCIAL_DECISION" && card.metadata && (
            <FinancialDecisionCardBody meta={card.metadata} />
          )}
          {/* E2E-04C: Projected card type bodies */}
          {card.card_type === "CUSTOMER_COMMUNICATION" && card.metadata && (
            <ProjectedCardBody meta={card.metadata} type="CUSTOMER_COMMUNICATION" label="Communication Reference" />
          )}
          {card.card_type === "DELIVERY_EVIDENCE" && card.metadata && (
            <ProjectedCardBody meta={card.metadata} type="DELIVERY_EVIDENCE" label="Delivery Record Reference" />
          )}
          {card.card_type === "FINANCIAL_SUMMARY" && card.metadata && (
            <FinancialSnapshotCardBody meta={card.metadata} />
          )}
          {card.card_type === "OPERATIONAL_EXCEPTION" && card.metadata && (
            <ProjectedCardBody meta={card.metadata} type="OPERATIONAL_EXCEPTION" label="Incident Reference" warningColor="text-[#BA0517]" />
          )}
          {/* DCC-04A: Draft dispatch inline form */}
          {card.card_type === "MESSAGE" && showDispatch && (
            <form
              data-testid="dispatch-comm-form"
              onSubmit={handleDispatch}
              className="mt-3 p-3 bg-white border border-[#0176D3]/20 rounded-xl space-y-2.5"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold text-[#0176D3] uppercase tracking-wide">
                  Prepare Communication Draft
                </span>
                <span className="text-[9px] text-[#706E6B] bg-[#F3F3F3] px-1.5 py-0.5 rounded font-mono">
                  DRAFT ONLY — you send it manually
                </span>
              </div>
              <div className="flex gap-2">
                {["EMAIL", "WHATSAPP"].map(ch => (
                  <label key={ch} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${
                    dispatchForm.channel === ch
                      ? "bg-[#0176D3]/10 border-[#0176D3] text-[#0176D3]"
                      : "bg-[#FAFAFA] border-[#C9C9C9] text-[#706E6B] hover:border-[#0176D3]/50"
                  }`}>
                    <input
                      type="radio"
                      name="channel"
                      value={ch}
                      checked={dispatchForm.channel === ch}
                      onChange={e => setDispatchForm(f => ({ ...f, channel: e.target.value, recipient_email: "" }))}
                      className="sr-only"
                    />
                    {ch}
                  </label>
                ))}
              </div>
              <input
                data-testid="dispatch-recipient-input"
                type={dispatchForm.channel === "EMAIL" ? "email" : "tel"}
                placeholder={dispatchForm.channel === "EMAIL" ? "Recipient email *" : "WhatsApp number *"}
                value={dispatchForm.recipient_email}
                onChange={e => setDispatchForm(f => ({ ...f, recipient_email: e.target.value }))}
                required
                className="w-full px-3 py-1.5 text-xs border border-[#C9C9C9] rounded-lg focus:outline-none focus:border-[#0176D3] bg-white"
              />
              <input
                data-testid="dispatch-name-input"
                type="text"
                placeholder="Recipient name (optional)"
                value={dispatchForm.recipient_name}
                onChange={e => setDispatchForm(f => ({ ...f, recipient_name: e.target.value }))}
                className="w-full px-3 py-1.5 text-xs border border-[#C9C9C9] rounded-lg focus:outline-none focus:border-[#0176D3] bg-white"
              />
              <input
                data-testid="dispatch-subject-input"
                type="text"
                placeholder="Subject"
                value={dispatchForm.subject}
                onChange={e => setDispatchForm(f => ({ ...f, subject: e.target.value }))}
                className="w-full px-3 py-1.5 text-xs border border-[#C9C9C9] rounded-lg focus:outline-none focus:border-[#0176D3] bg-white"
              />
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="dispatch-save-draft-btn"
                  type="submit"
                  disabled={dispatching}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#0176D3] text-white rounded-lg hover:bg-[#0176D3]/90 transition-colors disabled:opacity-50"
                >
                  {dispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Save as Draft
                </button>
                <button
                  type="button"
                  onClick={() => setShowDispatch(false)}
                  className="px-3 py-1.5 text-xs text-[#706E6B] border border-[#C9C9C9] rounded-lg hover:text-[#181818] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {/* CHECK_IN specific detail */}
          {card.card_type === "CHECK_IN" && card.metadata && (
            <div className="mt-2 p-2 bg-teal-950/30 border border-[#2E844A]/20 rounded-lg space-y-1">
              <div className="flex items-center gap-2">
                <UserCheck className="w-3 h-3 text-[#2E844A] flex-shrink-0" />
                <span className="text-xs text-[#2E844A] font-medium">
                  {card.metadata.check_in_type?.replace(/_/g, " ")}
                </span>
                {card.status === "VOIDED" && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#FCEBEC] text-[#BA0517] rounded font-mono">VOIDED</span>
                )}
              </div>
              {card.metadata.location_text && (
                <div className="flex items-center gap-1 text-[10px] text-[#706E6B]">
                  <MapPin className="w-2.5 h-2.5" /> {card.metadata.location_text}
                </div>
              )}
              <p className="text-[10px] text-[#706E6B]">
                Checked in at {card.metadata.checked_in_at?.slice(0, 16).replace("T", " ")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Compose Bar ───────────────────────────────────────────────────────────────
function ComposeBar({ threadId, onPosted }) {
  const [content, setContent] = useState("");
  const [type, setType] = useState("MESSAGE");
  const [posting, setPosting] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInForm, setCheckInForm] = useState({ participant_name: "", check_in_type: "ARRIVAL", location_text: "" });

  async function handlePost() {
    if (!content.trim()) return;
    setPosting(true);
    try {
      if (type === "MESSAGE") {
        await api.post(`/threads/${threadId}/messages`, { content });
      } else if (type === "TASK") {
        await api.post(`/threads/${threadId}/tasks`, { title: content });
      } else if (type === "ISSUE") {
        await api.post(`/threads/${threadId}/issues`, { title: content });
      }
      setContent("");
      onPosted();
      toast.success(`${type} added`);
    } catch { toast.error("Failed to post"); }
    finally { setPosting(false); }
  }

  async function handleCheckIn() {
    if (!checkInForm.participant_name.trim()) { toast.error("Participant name required"); return; }
    setPosting(true);
    try {
      await api.post(`/threads/${threadId}/checkins`, checkInForm);
      setCheckInForm({ participant_name: "", check_in_type: "ARRIVAL", location_text: "" });
      setShowCheckIn(false);
      onPosted();
      toast.success("Check-in recorded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check-in failed");
    } finally { setPosting(false); }
  }

  return (
    <div className="border-t border-[#E5E5E5] p-3 flex-shrink-0 bg-white">
      {showCheckIn ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#2E844A] flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5" /> Record Check-In
            </span>
            <button onClick={() => setShowCheckIn(false)} className="text-[#706E6B] hover:text-[#444444]">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <input
            data-testid="checkin-participant-input"
            value={checkInForm.participant_name}
            onChange={e => setCheckInForm(f => ({ ...f, participant_name: e.target.value }))}
            placeholder="Name (e.g. Decor crew, Ravi Kumar)"
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg px-3 py-2 text-sm text-[#181818] placeholder-[#C9C9C9] outline-none focus:ring-1 focus:ring-teal-500"
          />
          <div className="flex gap-2">
            <select
              data-testid="checkin-type-select"
              value={checkInForm.check_in_type}
              onChange={e => setCheckInForm(f => ({ ...f, check_in_type: e.target.value }))}
              className="flex-1 bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg px-2 py-1.5 text-xs text-[#444444]"
            >
              {["ARRIVAL", "CREW_ARRIVAL", "PARTNER_ARRIVAL", "RESOURCE_ARRIVAL", "SERVICE_START"].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
            <input
              value={checkInForm.location_text}
              onChange={e => setCheckInForm(f => ({ ...f, location_text: e.target.value }))}
              placeholder="Location (optional)"
              className="flex-1 bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg px-2 py-1.5 text-xs text-[#444444] placeholder-[#C9C9C9] outline-none"
            />
          </div>
          <button
            data-testid="checkin-submit"
            onClick={handleCheckIn}
            disabled={posting}
            className="w-full py-1.5 bg-teal-600/80 text-white rounded-lg text-xs font-medium hover:bg-teal-600 disabled:opacity-50 transition-colors"
          >
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Confirm Check-In"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            {["MESSAGE", "TASK", "ISSUE"].map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${type === t ? "bg-[#EEF4FF] text-[#0176D3]" : "text-[#706E6B] hover:text-[#444444]"}`}
              >
                {t}
              </button>
            ))}
            <button
              data-testid="checkin-toggle-btn"
              onClick={() => setShowCheckIn(true)}
              className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium text-teal-500 hover:text-[#2E844A] border border-[#2E844A]/20 hover:bg-[#EAF5EE] transition-colors"
            >
              <UserCheck className="w-3 h-3" /> Check In
            </button>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="compose-input"
              value={content}
              onChange={e => setContent(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handlePost()}
              placeholder={`Add ${type.toLowerCase()}...`}
              className="flex-1 bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg px-3 py-2 text-sm text-[#181818] placeholder-[#C9C9C9] outline-none focus:ring-1 focus:ring-[#0176D3]"
            />
            <button
              data-testid="compose-submit"
              onClick={handlePost}
              disabled={!content.trim() || posting}
              className="px-3 py-2 bg-[#0176D3] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Thread Summary Panel ──────────────────────────────────────────────────────
function ThreadSummaryPanel({ thread }) {
  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold text-[#706E6B] uppercase tracking-wide">Thread Summary</h3>
      <div className="space-y-2">
        {[
          { label: "Status", value: thread.status },
          { label: "Type", value: thread.thread_type?.replace(/_/g, " ") },
          { label: "Visibility", value: thread.visibility_class },
          { label: "Cards", value: thread.card_count },
        ].map(item => (
          <div key={item.label}>
            <p className="text-[10px] text-[#706E6B] uppercase">{item.label}</p>
            <p className="text-xs text-[#444444] font-medium">{item.value}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2 pt-2 border-t border-[#E5E5E5]">
        {thread.open_tasks > 0 && <StatRow label="Open Tasks" value={thread.open_tasks} color="text-[#0176D3]" />}
        {thread.open_issues > 0 && <StatRow label="Open Issues" value={thread.open_issues} color="text-[#BA0517]" />}
        {thread.pending_approvals > 0 && <StatRow label="Pending Approvals" value={thread.pending_approvals} color="text-[#8C4B00]" />}
      </div>
    </div>
  );
}

function ThreadTypeBadge({ type }) {
  const colors = {
    CUSTOMER: "text-[#0176D3]",
    INTERNAL_OPERATIONS: "text-[#706E6B]",
    FINANCE: "text-[#2E844A]",
    SERVICE: "text-[#5E4DB2]",
  };
  return (
    <span className={`text-[9px] font-medium uppercase ${colors[type] || "text-[#706E6B]"}`}>
      {type === "SERVICE" ? "SVC" : type?.split("_")[0]}
    </span>
  );
}

function ThreadStatusDot({ status }) {
  const dots = { ACTIVE: "bg-emerald-400", WAITING: "bg-amber-400", AT_RISK: "bg-red-400", COMPLETED: "bg-slate-500" };
  return <span className={`w-1.5 h-1.5 rounded-full ${dots[status] || "bg-slate-500"}`} />;
}

function StatusBadge({ status, cardType }) {
  const styles = {
    OPEN: "text-[#8C4B00] bg-[#FFF4E5]",
    IN_PROGRESS: "text-[#0176D3] bg-[#EEF4FF]",
    PENDING: "text-[#8C4B00] bg-[#FFF4E5]",
    APPROVED: "text-[#2E844A] bg-[#EAF5EE]",
    REJECTED: "text-[#BA0517] bg-[#FCEBEC]",
    COMPLETED: "text-[#2E844A] bg-[#EAF5EE]",
    RESOLVED: "text-[#706E6B] bg-[#FAFAFA]",
    CAPTURED: "text-[#0176D3] bg-[#EEF4FF]",
    DECIDED: "text-[#5E4DB2] bg-[#F1ECFF]",
    SENT: "text-[#706E6B]",
    COMPLETE: "text-[#706E6B]",
  };
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${styles[status] || "text-[#706E6B] bg-[#FAFAFA]"}`}>
      {status}
    </span>
  );
}

function Counter({ label, value, color }) {
  return (
    <span className={`text-[9px] font-medium ${color}`}>
      {value} {label}
    </span>
  );
}

function StatRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-[#706E6B]">{label}</span>
      <span className={`text-xs font-bold ${color}`}>{value}</span>
    </div>
  );
}


// ── WVB-04: FINANCIAL_DECISION fully styled card body ────────────────────────

const DECISION_STYLE = {
  APPROVED:         { label: "Approved",         bg: "bg-[#EAF5EE]", color: "text-[#2E844A]",  border: "border-[#2E844A]/20" },
  REJECTED:         { label: "Rejected",         bg: "bg-[#FCEBEC]", color: "text-[#BA0517]",  border: "border-[#BA0517]/20" },
  CHANGES_REQUIRED: { label: "Changes Required", bg: "bg-[#FFF4E5]", color: "text-[#8C4B00]",  border: "border-[#8C4B00]/20" },
};

function FinancialDecisionCardBody({ meta }) {
  const style = DECISION_STYLE[meta.decision] || DECISION_STYLE.APPROVED;
  const decidedAt = meta.decided_at
    ? new Date(meta.decided_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div
      data-testid={`financial-decision-card-${meta.approval_request_id}`}
      className={`mt-2 rounded-lg border p-3 ${style.bg} ${style.border}`}
    >
      {/* Decision badge + total */}
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${style.bg} ${style.color} border ${style.border}`}>
          {style.label}
        </span>
        <span className="text-xs font-semibold text-[#1C1917]">{meta.total_display}</span>
      </div>

      {/* Budget + approval refs */}
      <div className="mt-2 space-y-0.5">
        {meta.budget_number && (
          <div className="flex justify-between">
            <span className="text-[10px] text-[#706E6B]">Budget</span>
            <span className="text-[10px] font-medium text-[#1C1917]">{meta.budget_number} v{meta.budget_version}</span>
          </div>
        )}
        {meta.approval_number && (
          <div className="flex justify-between">
            <span className="text-[10px] text-[#706E6B]">Request</span>
            <span className="text-[10px] font-medium text-[#1C1917]">{meta.approval_number}</span>
          </div>
        )}
        {meta.decided_by_name && (
          <div className="flex justify-between">
            <span className="text-[10px] text-[#706E6B]">By</span>
            <span className="text-[10px] font-medium text-[#1C1917]">{meta.decided_by_name}</span>
          </div>
        )}
        {decidedAt && (
          <div className="flex justify-between">
            <span className="text-[10px] text-[#706E6B]">At</span>
            <span className="text-[10px] text-[#706E6B]">{decidedAt}</span>
          </div>
        )}
      </div>

      {/* Comments / reason */}
      {(meta.reason || meta.comments) && (
        <p className={`mt-2 text-xs border-t pt-2 ${style.border} ${style.color}`}>
          {meta.reason || meta.comments}
        </p>
      )}
    </div>
  );
}

// ── E2E-04C: Projected Card Body Components ───────────────────────────────────

/**
 * Generic projected card body — shows canonical source reference.
 * Never duplicates payload — only shows source_type + source_id + key metadata.
 */
function ProjectedCardBody({ meta, label, warningColor }) {
  return (
    <div className="mt-2 rounded-lg border border-[#E5E5E5] bg-white p-2.5 text-[10px] font-mono space-y-1">
      <p className="text-[#706E6B] font-semibold uppercase tracking-wide text-[9px]">{label}</p>
      {meta.source_type && (
        <div className="flex justify-between gap-3">
          <span className="text-[#706E6B]">Source Type</span>
          <span className="font-medium text-[#181818]">{meta.source_type}</span>
        </div>
      )}
      {meta.source_id && (
        <div className="flex justify-between gap-3">
          <span className="text-[#706E6B]">Source ID</span>
          <span className={`font-medium truncate max-w-[140px] ${warningColor || "text-[#0176D3]"}`} title={meta.source_id}>
            {meta.source_id}
          </span>
        </div>
      )}
      {meta.WARNING && (
        <p className="text-[9px] text-[#BA0517] bg-[#FCEBEC] px-1.5 py-0.5 rounded border border-[#BA0517]/20">
          {meta.WARNING}
        </p>
      )}
    </div>
  );
}

/**
 * FINANCIAL_SUMMARY card body — shows EFIN snapshot metrics.
 * Explicitly labelled as snapshot to prevent misrepresentation as live total.
 */
function FinancialSnapshotCardBody({ meta }) {
  function fmtMinor(v) {
    if (v == null) return "—";
    return `₹${(v / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return (
    <div className="mt-2 rounded-lg border border-[#5E4DB2]/20 bg-[#FAF8FF] p-3 space-y-1">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[9px] font-semibold text-[#5E4DB2] uppercase tracking-wide font-mono">
          EFIN Snapshot — {meta.snapshot_as_of?.slice(0, 10) || "—"}
        </p>
        <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded font-mono">SNAPSHOT — NOT LIVE</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] font-mono">
        <div className="flex justify-between"><span className="text-[#706E6B]">Invoiced AR</span><span className="font-medium text-[#181818]">{fmtMinor(meta.invoiced_revenue_minor)}</span></div>
        <div className="flex justify-between"><span className="text-[#706E6B]">Outstanding AR</span><span className="font-medium text-[#181818]">{fmtMinor(meta.outstanding_ar_minor)}</span></div>
        <div className="flex justify-between"><span className="text-[#706E6B]">Committed AP</span><span className="font-medium text-[#181818]">{fmtMinor(meta.committed_cost_minor)}</span></div>
        <div className="flex justify-between"><span className="text-[#706E6B]">Outstanding AP</span><span className="font-medium text-[#181818]">{fmtMinor(meta.outstanding_ap_minor)}</span></div>
        <div className="flex justify-between col-span-2 border-t border-[#5E4DB2]/10 pt-1 mt-0.5">
          <span className="text-[#5E4DB2]">Net Cash</span>
          <span className="font-bold text-[#5E4DB2]">{fmtMinor(meta.net_cash_movement_minor)}</span>
        </div>
      </div>
      {meta.snapshot_note && (
        <p className="text-[9px] text-[#706E6B] italic mt-1">{meta.snapshot_note}</p>
      )}
    </div>
  );
}

